'use client';

import { MarketType, UserAccount } from '@drift-labs/sdk';
import { PublicKey } from '@solana/web3.js';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { getEmulationParams, isValidPublicKey } from 'src/utils/routerUtils';
import { getStoredReferrerParam } from 'src/utils/referral';
import { getMarketFromUrl } from 'src/utils/market';
import { NonTradeRoutes, PageRoutes } from '../constants/constants';
import { DriftWindow } from '../window/driftWindow';
import usePostHogCapture from './posthog/usePostHogCapture';
import useAccountExists from './useAccountExists';
import { useSwitchMarket } from 'src/hooks/driftActions/market/useSwitchMarket';
import useDriftClientIsReady from './useDriftClientIsReady';
import useEmulateAccount from './wallet-management/useEmulateAccount';
import { usePrivyWalletContext } from 'src/providers/wallets/PrivyWalletProvider';

const useRouterConfig = (): null => {
	const [hasHandledReferrerParam, setHasHandledReferrerParam] = useState(false);
	const set = useDriftStore((s) => s.set);
	const get = useDriftStore((s) => s.get);
	const { emulateAccount } = useEmulateAccount();
	const { captureEvent } = usePostHogCapture();
	const driftClientIsReady = useDriftClientIsReady();
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const accountExists = useAccountExists();
	const { switchMarket } = useSwitchMarket();
	const { isPrivyNativeAuth } = usePrivyWalletContext();

	/**
	 * We use this flag to denote "If the wallet was going to auto-connect, it would have by now".
	 */
	const [autoconnectTimeoutElapsed, setAutoconnectTimeoutElapsed] =
		useState(false);

	// Logic to set initial market selection when navigating to a particular market route

	const alreadyUsedRouteParams = useRef(false);

	const handleAuthorityEmulation = async (
		authorityParam: string | string[]
	) => {
		if (!isValidPublicKey(authorityParam.toString())) {
			console.log('no authority: ' + authorityParam);
			return;
		}

		if (authorityParam.includes(',')) {
			set((s) => {
				s.authorityParam = `authority=${authorityParam}`;
			});
		} else {
			const authority = new PublicKey(authorityParam);
			set((s) => {
				s.authorityParam = `authority=${authority.toString()}`;
			});
			emulateAccount({ authority });
		}
	};

	const handleUserAccountEmulation = async (userAccountParam: string) => {
		try {
			const driftClient = get().driftClient.client;

			const userAccountPubKey = new PublicKey(userAccountParam);

			const userAccount = (await driftClient?.program.account.user.fetch(
				userAccountPubKey
			)) as UserAccount;

			const authority = userAccount.authority;

			set((s) => {
				s.authorityParam = `authority=${authority.toString()}`;
			});

			emulateAccount({
				authority,
				subAccountPubKey: userAccountPubKey,
			});
		} catch (e) {
			console.log(
				'Error emulating account using user account key: ' + userAccountParam,
				e
			);
		}
	};

	const handleReferralPageLoad = async (
		referrerParam: string,
		fromLocalStorage?: boolean
	) => {
		const driftClient = get().driftClient.client;

		let referrerNameExists: boolean;
		try {
			const referrerNameAccount =
				await driftClient?.fetchReferrerNameAccount(referrerParam);
			referrerNameExists = !!referrerNameAccount;
		} catch (err) {
			referrerNameExists = false;
		}

		if (referrerNameExists && !accountExists) {
			if (!fromLocalStorage) {
				window?.localStorage?.setItem('referrerParam', referrerParam);
				window?.localStorage?.setItem(
					'referrerAppliedTimestamp',
					Date.now().toString()
				);
			}

			set((s) => {
				s.referrerParam = referrerParam;
			});
		} else {
			set((s) => {
				s.referrerParamFailed = true;
			});
		}
	};

	useEffect(() => {
		setTimeout(() => {
			setAutoconnectTimeoutElapsed(true);
		}, 5000);
	}, []);

	// Handle pushing posthog pageview event on route change
	useEffect(() => {
		if (pathname) {
			captureEvent('$pageview', {
				$current_url: window.location.href,
			});
		}
	}, [pathname]);

	useEffect(() => {
		const isNotTradePage = NonTradeRoutes.find((nonTradeRoute) =>
			pathname?.includes(nonTradeRoute)
		);

		const isTradePage = !isNotTradePage;

		// shouldTrackTradePageStartupTimeMetrics is a oneWayValueJar so it will only let us set the value ONCE. This prevents us changing the value multiple times as this router instance is re-mounted. We only want to set this value based on the initial page route.
		DriftWindow.shouldTrackTradePageStartupTimeMetrics.set(isTradePage);

		const pageRoute = PageRoutes.find((pageRoute) =>
			pathname?.includes(pageRoute)
		);

		if (pageRoute !== undefined) {
			set((s) => {
				const routeChanged = s.currentPageRoute !== pageRoute;

				if (routeChanged) {
					s.currentPageRoute = pageRoute;
				}
			});
		}
	}, [pathname]);

	// Handle query params from router
	useEffect(() => {
		const storedReferrer = getStoredReferrerParam();

		const referralMatch = pathname?.match(/ref\/(.+)($|\/)/);
		if (referralMatch?.[1]) {
			if (
				driftClientIsReady &&
				autoconnectTimeoutElapsed &&
				!hasHandledReferrerParam
			) {
				handleReferralPageLoad(referralMatch[1] as string);
				setHasHandledReferrerParam(true);
			}
		} else if (
			storedReferrer &&
			driftClientIsReady &&
			autoconnectTimeoutElapsed &&
			!hasHandledReferrerParam &&
			!searchParams?.get('ref')
		) {
			handleReferralPageLoad(storedReferrer, true);
			setHasHandledReferrerParam(true);
		}

		if (searchParams || pathname) {
			// don't execute rest of logic if user onboarding
			if (pathname?.includes('/connect')) {
				set((s) => {
					s.authorityParam = '';
				});
				return;
			}

			const targetMarket = getMarketFromUrl(
				pathname ?? '',
				searchParams ?? new URLSearchParams()
			);

			if (targetMarket) {
				// Redirect to perps when Privy-native for now.
				if (isPrivyNativeAuth && targetMarket.marketType === MarketType.SPOT) {
					switchMarket({ marketIndex: 0, marketType: MarketType.PERP });
				} else {
					switchMarket({
						marketIndex: targetMarket.marketIndex,
						marketType: targetMarket.marketType,
					});
				}
			}

			// # Handle referrer param
			if (searchParams?.get('ref')) {
				if (
					driftClientIsReady &&
					autoconnectTimeoutElapsed &&
					!hasHandledReferrerParam
				) {
					handleReferralPageLoad(searchParams.get('ref') as string);
					setHasHandledReferrerParam(true);
				}
			}

			if (alreadyUsedRouteParams.current) return;

			const emulationParams = getEmulationParams(searchParams);

			if (emulationParams && driftClientIsReady) {
				alreadyUsedRouteParams.current = true;

				if (emulationParams.type === 'authority') {
					handleAuthorityEmulation(emulationParams.value);
				} else {
					handleUserAccountEmulation(emulationParams.value);
				}
			} else {
				set((s) => {
					s.authorityParam = '';
				});
			}
		} else {
			set((s) => {
				s.authorityParam = '';
			});
		}
	}, [
		searchParams,
		driftClientIsReady,
		autoconnectTimeoutElapsed,
		hasHandledReferrerParam,
		isPrivyNativeAuth,
	]);

	return null;
};

export default useRouterConfig;
